import React from "react";
import { CirclePlus, TriangleAlert, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";

const BODY_TYPES = [
  { label: "none", value: "none" },
  { label: "raw", value: "raw" },
  { label: "x-www-form-urlencoded", value: "form-urlencoded" },
];

type BodyState = {
  bodyType: string;
  rawBodyType: string;
  body: string;
  bodyValidationError: string | null;
  formData: { key: string; value: string; enabled: boolean }[];
};

type BodyHandlers = {
  handleBodyTypeChange: (value: string) => void;
  handleRawBodyTypeChange: (value: string) => void;
  handleBodyChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleFormDataChange: (
    index: number,
    field: "key" | "value" | "enabled",
    value: string | boolean,
  ) => void;
  addFormDataRow: () => void;
  removeFormDataRow: (index: number) => void;
};

type props = {
  bodyState: BodyState;
  bodyHandlers: BodyHandlers;
};

const QueryBuilderBody: React.FC<props> = ({ bodyState, bodyHandlers }) => {
  const { bodyType, rawBodyType, body, bodyValidationError, formData } =
    bodyState;
  const bodyTypeRadioGroupId = React.useId();

  const {
    handleBodyTypeChange,
    handleRawBodyTypeChange,
    handleBodyChange,
    handleFormDataChange,
    addFormDataRow,
    removeFormDataRow,
  } = bodyHandlers;

  const lineNumbersRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Synchronize scroll between line numbers and textarea
  const handleScroll = () => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div className="mt-4 ">
      <p className="ml-1 text-sm font-semibold text-amber">Body</p>

      <div className="flex justify-between items-center h-6 mt-1">
        <div>
          <div className="w-full flex items-center gap-3 h-5">
            {/* body type */}
            <div className="flex items-center justify-end ml-1">
              <RadioGroup
                value={bodyType}
                onValueChange={handleBodyTypeChange}
                className="flex"
              >
                {BODY_TYPES.map(({ label, value }) => (
                  <div key={value} className="flex items-center gap-1">
                    {(() => {
                      const optionId = `${bodyTypeRadioGroupId}-${value}`;
                      return (
                        <>
                          <RadioGroupItem
                            value={value}
                            id={optionId}
                            className="border-primaryAmber data-[state=checked]:text-primaryAmber h-4 w-4"
                          />
                          <Label
                            htmlFor={optionId}
                            className="text-xs cursor-pointer"
                          >
                            {label}
                          </Label>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-4">
              {/* Raw Body Options */}
              {bodyType === "raw" && (
                <div className="flex items-center gap-3 text-primaryBlue ">
                  <Select
                    value={rawBodyType}
                    onValueChange={handleRawBodyTypeChange}
                    defaultValue="text"
                  >
                    <SelectTrigger className="h-5 min-w-[50px] text-xs bg-transparent">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="html">HTML</SelectItem>
                      <SelectItem value="javascript">JavaScript</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="xml">XML</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          {bodyValidationError && (
            <div className="text-xs text-primaryAmber flex items-center gap-1 cursor-defaul max-w-[400px]">
              <TriangleAlert size={17} strokeWidth={2.2} />
              <div>{bodyValidationError}</div>
            </div>
          )}
        </div>
      </div>

      {/* Raw Body Textarea */}
      {bodyType === "raw" && (
        <div className="w-full mt-2">
          <div className="relative border rounded-lg overflow-hidden">
            <div className="flex">
              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={body}
                onChange={handleBodyChange}
                onScroll={handleScroll}
                className={`flex-1 break-words p-3 font-mono bg-background text-muted-foreground text-sm min-h-[80px] max-h-[200px] resize-none overflow-auto font-semibold whitespace-pre-wrap border-none outline-none focus:ring-0 ${
                  bodyValidationError ? "bg-lightRed" : ""
                }`}
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: "1.5",
                }}
                placeholder={
                  rawBodyType === "json"
                    ? '{\n  "username": "{{config.username}}",\n  "categories": {{categoryArray}}\n}'
                    : rawBodyType === "xml"
                      ? "<root>\n  <username>{{config.username}}</username>\n</root>"
                      : rawBodyType === "html"
                        ? " <div>\n <span>{{config.username}}</span>\n</div>"
                        : `username={{config.username}}&user={{config.user_id}}`
                }
                name="body"
                spellCheck={false}
              />
            </div>

            {/* Line separators - for all existing lines */}
            {!bodyValidationError && (
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden"
                style={{
                  paddingLeft: lineNumbersRef.current?.offsetWidth || 0,
                  paddingTop: "12px",
                }}
              >
                {(body || "\n").split("\n").map((_, index) => (
                  <div
                    key={index}
                    className="border-b border-secondary/30"
                    style={{
                      height: "1.5em",
                      marginLeft: "12px",
                      marginRight: "12px",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {bodyValidationError && (
            <div className="text-xs text-primary mt-1 ml-1">
              {bodyValidationError}
            </div>
          )}
        </div>
      )}

      {/* Form Data */}
      {bodyType === "form-urlencoded" && (
        <div className="border rounded-lg p-2 bg-background mt-1">
          <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 mb-2 text-xs font-semibold text-muted-foreground">
            <div className="w-6 px-4"></div>
            <div>Key</div>
            <div>Value</div>
            <div className="w-6"></div>
          </div>
          {formData.map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 border-b items-center bg-muted p-1 rounded-md px-3"
            >
              <input
                type="checkbox"
                checked={item.enabled}
                onChange={(e) =>
                  handleFormDataChange(index, "enabled", e.target.checked)
                }
                className="h-4 w-4"
              />
              <input
                type="text"
                value={item.key}
                onChange={(e) =>
                  handleFormDataChange(index, "key", e.target.value)
                }
                placeholder="Key"
                className="border p-2 bg-background text-xs rounded-md"
              />
              <input
                type="text"
                value={item.value}
                onChange={(e) =>
                  handleFormDataChange(index, "value", e.target.value)
                }
                placeholder="value"
                className="border p-2 bg-background text-xs rounded-md"
              />
              <button
                onClick={() => removeFormDataRow(index)}
                className="text-destructive hover:text-destructive/80"
                disabled={formData.length === 1}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={addFormDataRow}
            className="text-sm text-primaryBlue hover:text-muted-foreground flex items-center gap-1 mt-3"
          >
            <CirclePlus size={14} /> Add Row
          </button>
        </div>
      )}
    </div>
  );
};

export default QueryBuilderBody;
